<template>
  <div>
    <!-- Product first: the two products are different books of business, so
         the counts below are only meaningful once one is chosen. -->
    <!-- Narrowed to the tab this user is allowed when an admin has pinned their
         product type, rather than hidden: the count is theirs to see, and a
         missing strip takes it away. ProductSwitch draws whatever it is given. -->
    <ProductSwitch
      :value="product"
      :counts="visibleProductCounts"
      @change="selectProduct"
    />

    <!-- The stages ARE the filter: press one to see only those customers,
         press "All customers" (or the pressed one again) to clear. The same
         strip Products and User Management use -- one bar rather than seven
         cards, and one colour rather than seven, so the figures read as slices
         of a single total instead of seven unrelated things. Order is the
         progression: everyone, then untouched offers, then the ones being
         acted on, then the two end states.

         Always drawn, the same as User Management. Until the first counts land
         -- still loading, or failed with nothing to keep -- the cells come from
         the canonical stage list (see bucketItems), so the strip shows
         skeletons while it waits and dashes if it gives up, and never
         collapses to a lone "All customers" stretched across the page. It used
         to be hidden until the counts arrived, which on a cold cache or during
         the precheck index rebuild meant many seconds with no tabs at all. -->
    <StatStrip
      :items="bucketItems"
      :active="stage"
      :loading="countsLoading"
      @select="selectStage"
    />

    <!-- Search sits with the filters it works alongside, the same row as Date
         range and City, matching User Management. The navbar's own box is
         switched off for this route (meta.show_global_search) so there is one
         search control, not two competing for the same query. -->
    <FilterBar
      :search.sync="search"
      search-placeholder="Search customer, mobile or offer"
      :date-range="dateRange"
      :advanced-count="activeChips.length"
      :rest-count="restCount"
      :show-date="!filterLocked('offer-management', 'date_range')"
      @update:dateRange="onDateRange"
      @change="reload"
      @reset="resetFilters"
    >
      <!--
        One ordered list rather than a fixed visible row plus an `advanced`
        slot. A filter can be hidden -- an admin can lock it for a team member
        in Team -> Filtered view -- and with two fixed groups the hidden one
        left an empty cell while its replacement stayed shut behind the toggle.
        Ordered, whatever is hidden simply drops out and the next filter takes
        the vacated track. `filterLayout` decides which land in the row that
        stays on screen; the rest carry `kf-field--rest`.
      -->
      <template v-for="f in filterLayout">
        <div
          :key="f.key"
          class="kf-field"
          :class="{ 'kf-field--rest': f.rest }"
        >
          <span class="kf-field__label">{{ f.label }}</span>

          <MultiSelect
            v-if="f.key === 'city'"
            v-model="filters.city"
            :items="options.cities"
            @change="reload"
          />
          <MultiSelect
            v-else-if="f.key === 'state'"
            v-model="filters.state"
            :items="options.states"
            @change="reload"
          />
          <MultiSelect
            v-else-if="f.key === 'lead_partner'"
            v-model="filters.lead_partner"
            :items="options.lead_partners"
            @change="reload"
          />
          <v-select
            v-else-if="f.key === 'availed_offer'"
            v-model="filters.availed_offer"
            :items="options.availed_offer_counts"
            item-text="name"
            return-object
            placeholder="Any"
            dense outlined single-line clearable hide-details
            @change="reload"
          />
          <DateField
            v-else-if="f.key === 'applied_range'"
            v-model="filters.applied_range"
            range
            @change="reload"
          />
          <DateField
            v-else-if="f.key === 'disbursal_range'"
            v-model="filters.disbursal_range"
            range
            @change="reload"
          />
          <DateField
            v-else-if="f.key === 'follow_up_date'"
            v-model="filters.follow_up_date"
            @change="reload"
          />
          <v-select
            v-else-if="f.key === 'is_assigned'"
            v-model="filters.is_assigned"
            :items="options.agent_types"
            placeholder="Any"
            dense outlined single-line clearable hide-details
            @change="onAgentTypeChange"
          />
          <v-autocomplete
            v-else-if="f.key === 'agent_search_query'"
            v-model="filters.agent"
            :items="options.agents"
            item-text="name"
            return-object
            placeholder="Any agent"
            dense outlined single-line clearable hide-details
            @change="reload"
          />
          <v-select
            v-else-if="f.key === 'document_type'"
            v-model="filters.document_type"
            :items="options.document_types"
            placeholder="Any"
            dense outlined single-line clearable hide-details
            @change="onDocTypeChange"
          />
          <MultiSelect
            v-else-if="f.key === 'document_request_status'"
            v-model="filters.document_request_status"
            :items="docStatusItems"
            @change="reload"
          />
          <MultiSelect
            v-else-if="f.key === 'perfios_status'"
            v-model="filters.perfios_status"
            :items="options.perfios_statuses"
            @change="reload"
          />
          <!-- Attribution. Six of the same control, so they share one branch
               keyed on the filter name rather than six copies of it: the spec
               key IS the singular of its option list. Same arrangement as User
               Management. -->
          <MultiSelect
            v-else
            v-model="filters[f.key]"
            :items="options[utmOptionKey(f.key)]"
            @change="reload"
          />
        </div>
      </template>

      <!-- A filtered-down table always says why. -->
      <template #chips>
        <v-chip
          v-for="c in activeChips"
          :key="c.key"
          small
          close
          class="kf-fchip"
          @click:close="clearFilter(c.key)"
        >
          <strong>{{ c.label }}:</strong>&nbsp;{{ c.value }}
        </v-chip>
      </template>
    </FilterBar>

    <!-- One row per CUSTOMER, with that customer's offers behind the chevron.
         The parent row answers every column on its own, so a collapsed row is
         still a complete row; the sub-table below it mirrors the parent's
         colgroup so each offer value sits under the heading that names it. -->
    <DataTable
      class="kf-apps-table"
      :title="stage ? displayLabel(stage) + ' — customers' : 'All customers'"
      :headers="headers"
      :items="items"
      :loading="loading"
      :error="error"
      :page="page"
      :total-pages="totalPages"
      :total-count="totalCount"
      :page-length="pageLength"
      item-key="id"
      show-expand
      :expanded.sync="expanded"
      empty-title="No customers match these filters"
      empty-message="Try selecting a different stage or widening the date range."
      empty-icon="file-text"
      @page-change="onPage"
      @retry="reload"
      @row-click="toggleRow"
    >
      <template #actions>
        <DownloadReport :action="downloadReport" />
      </template>
      <!-- Parent row: the customer's own answer for every column, so a
           collapsed row is still a complete row. -->
      <template #[`item.name`]="{ item }">
        <div class="kf-user">
          <span class="kf-user__avatar">{{ fmt.initials(item.name) }}</span>
          <div class="kf-user__id">
            <div class="kf-user__name">{{ item.name }}</div>
            <div class="kf-user__sub">
              {{ fmt.mobile(item.mobile) }} ·
              {{ item.offer_count }}
              {{ item.offer_count === 1 ? "offer" : "offers" }}
            </div>
          </div>
        </div>
      </template>

      <!-- The customer's own facts, which the row used to send you to the
           customer detail page to read. -->
      <template #[`item.city`]="{ item }">
        <div class="kf-stack">
          <div class="kf-truncate" :title="item.city">{{ fmt.orDash(item.city) }}</div>
          <div class="kf-stack__sub">{{ fmt.orDash(item.pincode) }}</div>
        </div>
      </template>

      <template #[`item.age`]="{ item }">
        <div v-if="item.age" class="kf-stack">
          <div>{{ item.age }}</div>
          <div v-if="item.gender" class="kf-stack__sub">{{ item.gender }}</div>
        </div>
        <span v-else class="kf-muted">-</span>
      </template>

      <template #[`item.salary`]="{ item }">
        <div v-if="item.salary" class="kf-stack kf-stack--end">
          <div>
            <span class="kf-salary">{{ fmt.currency(item.salary) }}</span>
            <span class="kf-salary__unit">/mo</span>
          </div>
        </div>
        <span v-else class="kf-muted">-</span>
      </template>

      <!-- This customer's OTHER loans. Both counts at zero is a fact about a
           first-time borrower, not missing data, so it is stated rather than
           shown as "0 / 0". -->
      <template #[`item.loans`]="{ item }">
        <div v-if="item.loans_completed || item.loans_rejected" class="kf-stack">
          <div>{{ item.loans_completed }} done</div>
          <div class="kf-stack__sub">{{ item.loans_rejected }} rejected</div>
        </div>
        <span v-else class="kf-muted">None</span>
      </template>

      <!-- The rejection reason sits under the stage it explains, which is where
           it reads: as a column it was a stretch of dashes with an occasional
           truncated sentence a third of the way across the table. Kept on one
           line with the full text as the tooltip so a long reason cannot make
           one row taller than its neighbours. -->
      <template #[`item.stage`]="{ item }">
        <div class="kf-stack">
          <div><StatusChip :status="item.stage" small /></div>
          <div
            v-if="item.reject_reason"
            class="kf-stack__sub kf-truncate"
            :title="item.reject_reason"
          >
            {{ item.reject_reason }}
          </div>
        </div>
      </template>

      <template #[`item.clicked`]="{ item }">
        <span v-if="item.clicked_count" class="kf-yes">
          <LucideIcon name="circle-check" :size="13" color="#00796B" />
          {{ item.clicked_count }} of {{ item.offer_count }}
        </span>
        <span v-else class="kf-muted">None</span>
      </template>

      <template #[`item.applied_at`]="{ item }">
        <span class="kf-date">{{ fmt.date(item.applied_at) }}</span>
      </template>

      <template #[`item.last_modified`]="{ item }">
        <span class="kf-date" :title="fmt.dateTime(item.last_modified)">
          {{ fmt.relative(item.last_modified) }}
        </span>
      </template>

      <!-- Copy UTM link. The link belongs to an OFFER, not to a customer, and
           a customer row can hold none, one or several -- so one button cannot
           be one link. None: disabled. One: copies straight away. Several: a
           menu of that customer's offers, the same list the sub-rows show, so
           the row never has to be expanded to get a link out of it.
           @click.stop throughout, or every copy would also toggle the row. -->
      <template #[`item.actions`]="{ item }">
        <div class="kf-actions" @click.stop>
          <v-tooltip v-if="linkableOffers(item).length < 2" bottom>
            <template #activator="{ on, attrs }">
              <!-- The span keeps the tooltip alive on a disabled button, which
                   otherwise swallows the mouse events it listens for. -->
              <span v-bind="attrs" v-on="on">
                <v-btn
                  icon
                  small
                  :disabled="!linkableOffers(item).length"
                  @click.stop="copyUtmLink(linkableOffers(item)[0])"
                >
                  <LucideIcon name="copy" :size="16" />
                </v-btn>
              </span>
            </template>
            <span>{{ linkableOffers(item).length ? "Copy UTM link" : "No UTM link" }}</span>
          </v-tooltip>

          <v-menu v-else offset-y left>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" @click.stop>
                <LucideIcon name="copy" :size="16" />
              </v-btn>
            </template>
            <v-list dense>
              <v-subheader class="kf-menu-head">Copy UTM link</v-subheader>
              <v-list-item
                v-for="o in linkableOffers(item)"
                :key="o.id"
                @click="copyUtmLink(o)"
              >
                <v-list-item-title class="kf-menu">{{ o.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </template>

      <!-- @click.stop so opening the popup does not also toggle the row open.
           No optional chaining: vue-template-compiler 2.6 cannot parse `?.`. -->
      <template #[`item.utm`]="{ item }">
        <a class="kf-utm" @click.stop="ViewUtm(item)">
          {{ (item.utm && item.utm.utm_source) || "View" }}
        </a>
      </template>

      <!-- Offer sub-rows, sharing the parent's columns so each value sits
           under the heading that names it. -->
      <template #expanded-item="{ headers: cols, item }">
        <td :colspan="cols.length" class="kf-expanded">
          <table class="kf-subtable">
            <colgroup>
              <col v-for="c in cols" :key="c.value" :style="{ width: c.width + 'px' }" />
            </colgroup>
            <tbody>
              <tr v-for="o in offerLines(item)" :key="o.id || 'none'" class="kf-subrow">
                <td class="kf-subrow__rail"></td>
                <td>
                  <div class="kf-offer">
                    <img
                      v-if="o.logo"
                      class="kf-offer__logo"
                      :src="o.logo"
                      :alt="o.title"
                    />
                    <span v-else class="kf-offer__logo kf-offer__logo--none">
                      <LucideIcon name="tag" :size="14" color="#54636F" />
                    </span>
                    <span class="kf-offer__title" :title="o.title">
                      <template v-if="o.id !== null">{{ o.title }}</template>
                      <span v-else class="kf-muted">No offer yet</span>
                    </span>
                  </div>
                </td>
                <!-- City, Age, Income and Loans are facts about the CUSTOMER,
                     not about one of their offers, so they are left blank here
                     rather than filled with a dash. Four dashes in a row read as
                     data that failed to load; blank says the answer is on the
                     parent row one line above. The cells still have to exist --
                     the sub-table mirrors the parent's colgroup, and one short
                     and every offer value sits a column left of its heading. -->
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td>
                  <div class="kf-stack">
                    <div><StatusChip :status="o.stage" small /></div>
                    <div
                      v-if="o.reject_reason"
                      class="kf-stack__sub kf-truncate"
                      :title="o.reject_reason"
                    >
                      {{ o.reject_reason }}
                    </div>
                  </div>
                </td>
                <td>
                  <!-- Whether, not when: loan_offers has no click timestamp
                       column, so there is no date to render here. -->
                  <span v-if="o.clicked" class="kf-yes">
                    <LucideIcon name="circle-check" :size="13" color="#00796B" />
                    Clicked
                  </span>
                  <span v-else class="kf-muted">Not clicked</span>
                  <div v-if="o.repeat_of">
                    <span
                      class="kf-tag kf-tag--repeat"
                      :title="`Applied again for the same product within an hour of ${o.repeat_of}`"
                    >Repeat of {{ o.repeat_of }}</span>
                  </div>
                </td>
                <!-- The offer serializer excludes created/modified, so an
                     offer has no dates of its own. The application's dates are
                     on the parent row directly above. -->
                <td></td>
                <td>
                  <span v-if="o.sub_status" class="kf-muted">{{ o.sub_status }}</span>
                </td>
                <!-- Attribution is a fact about the CUSTOMER, not about one of
                     their offers, so it stays on the parent row. Blank, not a
                     dash -- but the cell must exist to keep the colgroup aligned. -->
                <td></td>
                <!-- Mirrors the parent's Copy column. Left blank: the parent
                     row's menu already names every offer in this sub-table, so
                     a second copy button per line would be the same action
                     twice. The cell still has to exist or every offer value
                     above shifts a column off its heading. -->
                <td></td>
              </tr>
            </tbody>
          </table>
        </td>
      </template>
    </DataTable>

    <!-- The same dialog the legacy screens use, unchanged: it takes no props
         and reads the six utm_* keys off whatever object openDialog is given. -->
    <ViewUTMDetail ref="UtmDialog" />
  </div>
</template>

<script>
import FilterBar from "@/components/karrofin/FilterBar.vue";
import ProductSwitch from "@/components/karrofin/ProductSwitch.vue";
import DataTable from "@/components/karrofin/DataTable.vue";
import StatStrip from "@/components/karrofin/StatStrip.vue";
import DownloadReport from "@/components/karrofin/DownloadReport.vue";
import StatusChip from "@/components/karrofin/StatusChip.vue";
import MultiSelect from "@/components/karrofin/MultiSelect.vue";
import DateField from "@/components/karrofin/DateField.vue";
import api from "@/app-js/karrofin/api";
import store, {
  defaultDateRange,
  productFromQuery,
} from "@/app-js/karrofin/store";
import fmt from "@/app-js/karrofin/format";
import copyToClipboard from "@/app-js/karrofin/clipboard";
import {
  PRODUCT_TYPES,
  APPLICATION_STAGE,
  APPLICATION_STAGES,
  displayLabel,
} from "@/app-js/karrofin/constants";
import {
  defaultFilters,
  toParams,
  activeFilters,
  clearOne,
} from "@/app-js/karrofin/filters";
import { PAGE_LENGTH } from "@/app-js/karrofin/config";

/* What a tile shows when its number could not be fetched -- an em dash rather
   than a 0, because zero is a fact and this is the absence of one. Same as
   User Management. */
const UNKNOWN = "—";

/**
 * Every filter this screen offers, in the order it should appear.
 *
 * The first entries are the ones that stay on screen with the panel collapsed;
 * everything after fills the panel. Order, not a fixed split, because a filter
 * can be hidden -- an admin can lock it for a team member -- and the next one
 * has to be able to move up into the vacated track.
 *
 * `key` is the LOCK's parameter name, which is what filterLocked() matches on.
 * `when` carries a condition the control already had: a filter that cannot be
 * rendered right now is left out of the ordering entirely, so it never holds a
 * cell it will not fill.
 */
const FILTER_ORDER = [
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "lead_partner", label: "Lead source" },
  { key: "availed_offer", label: "Availed offers" },
  /* Attribution. PRIMARY_SLOTS is 2, so only City and State sit beside Search
     and Date range; these open with "More filters". */
  { key: "utm_source", label: "Partner ID (utm_source)" },
  { key: "utm_medium", label: "Publisher (utm_medium)" },
  { key: "utm_campaign", label: "UTM Campaign" },
  { key: "utm_content", label: "Source (utm_content)" },
  { key: "utm_term", label: "UTM Term" },
  { key: "utm_platform", label: "UTM Platform" },
  { key: "applied_range", label: "Applied" },
  { key: "disbursal_range", label: "Disbursed" },
  { key: "follow_up_date", label: "Follow-up" },
  { key: "is_assigned", label: "Agent type" },
  // An agent picker under "Unassigned" is a control that can only return
  // nothing, so it waits for "Assigned".
  { key: "agent_search_query", label: "Agent", when: (vm) => vm.filters.is_assigned === true },
  { key: "document_type", label: "Document type" },
  // The type decides which statuses can occur.
  { key: "document_request_status", label: "Doc status", when: (vm) => !!vm.filters.document_type },
  { key: "perfios_status", label: "Perfios", when: (vm) => vm.showPerfios },
];

/**
 * Filters shown beside Search and Date range when the panel is collapsed.
 *
 * Two, which is what this screen shows today -- the row is four tracks wide and
 * FilterBar's own Search and Date range take the first two. Deliberately not
 * derived from the grid: keeping it fixed means an unrestricted user's bar is
 * unchanged, and only a hidden filter causes anything to move.
 */
const PRIMARY_SLOTS = 2;

/**
 * The stand-in line for a customer with no offers at all.
 *
 * An expanded row with nothing in it reads as a load that failed rather than
 * as a fact about that customer, so they get one line saying so. The `id` is
 * null, and that is what the "No offer yet" branch keys off -- every other
 * per-offer cell falls through to its own dash. Frozen and shared: it is never
 * written to, and one object means Vue does not re-key the row every render.
 */
const EMPTY_OFFER = Object.freeze({
  id: null,
  title: null,
  stage: null,
  clicked: false,
  disbursal_amount: null,
  loan_amount: null,
  reject_reason: null,
  logo: null,
});

export default {
  name: "KarroFinApplications",
  components: {
    FilterBar,
    ProductSwitch,
    DataTable,
    StatStrip,
    DownloadReport,
    StatusChip,
    MultiSelect,
    DateField,
    ViewUTMDetail: () => import("@/components/las/ViewUTMDetail.vue"),
  },
  data() {
    return {
      fmt,
      // Two flags, not one. The rows and the counts arrive from two separate
      // requests now (see api.getApplications), and the table must be allowed
      // to paint as soon as its own request lands rather than waiting for the
      // pipeline numbers behind it.
      loading: true,
      countsLoading: true,
      error: null,
      items: [],
      counts: [],
      page: 1,
      totalPages: 1,
      totalCount: 0,
      pageLength: PAGE_LENGTH,
      stage: this.$route.query.stage || null,
      // Local to the screen, like every other KarroFin filter. The navbar's
      // shared search state is cleared on sidebar clicks, which made this
      // screen's query disappear for reasons the screen could not see.
      search: "",
      // Local, not the shared store: that store can hold "All", which a
      // two-way switch cannot represent. Seeded from the query so a dashboard
      // drill-down lands on the product it came from.
      product: productFromQuery(this.$route.query.product, PRODUCT_TYPES),
      productCounts: PRODUCT_TYPES.map((label) => ({ label, count: 0 })),
      // Declared explicitly so the chevron sits at the LEFT of the customer,
      // where an expandable row expects it. Vuetify only appends its own when
      // this entry is absent.
      // Widths are explicit so the offer sub-rows can mirror them and every
      // value sits under the heading that names it.
      // These sum to 1364px, which is the min-width at the foot of this file --
      // keep the two in step. That number is a deliberate trade rather than a
      // ceiling: the set used to total 1102 so that a 1366px laptop -- which
      // leaves 1134px here once the 200px drawer and the 32px gutter are taken
      // -- never showed a horizontal scrollbar. The customer columns do not fit
      // in that, so the table is wider and scrolls below ~1520px.
      //
      // Score and Amount were dropped from this table and their 196px stayed in
      // the budget rather than shrinking it: Stage took 96 of it, Customer 60,
      // and City, Income and Loans the remaining 40. Those are the columns that
      // had been paying for the rest.
      //
      // Precheck was dropped later and its 166px stayed in the budget the same
      // way: Stage took 86 and Customer 80. Those two are the only cells here
      // holding text that still truncates -- the rejection reason under a Stage
      // chip, and the offer titles that render in the Customer column on the
      // expanded sub-rows. Every other column is sized to its own content, so
      // giving them width would only have added padding.
      //
      // Reason is filled on 15 rows in 50 and already truncated at 112px, and
      // the reason for a stage reads better directly under that stage than a
      // column away -- so it is a sub-line in the Stage cell rather than a
      // heading, and Stage is now wide enough to actually show one.
      headers: [
        { text: "", value: "data-table-expand", width: 46 },
        // 196 is the floor: below ~190 the sub-line "98565 56556 · 4 offers"
        // wraps, and a wrapped sub-line makes its row taller than every row
        // around it. The 140 above that floor is reclaimed width, spent here
        // because the offer titles on the expanded sub-rows render in this
        // column and clipped at 196.
        { text: "Customer", value: "name", width: 336 },
        // 108 is the floor: "Bengaluru" is the longest city in the data and
        // clipped at 100. The 18 above it is reclaimed width, so a city name
        // longer than any the dev data holds still fits.
        { text: "City", value: "city", width: 126 },
        // Age on top, gender under it. Two digits and a short word, so it is
        // the narrowest column in the table. Gender is empty on every row in the
        // dev database; the field is real, the data is simply not seeded, and
        // the cell drops to just the age rather than leaving a gap.
        { text: "Age", value: "age", width: 68 },
        { text: "Income", value: "salary", width: 112, align: "end" },
        // Sized for the sub-line "N rejected", not the "N done" above it -- at 90
        // the word itself was clipped mid-way.
        { text: "Loans", value: "loans", width: 108 },
        // The widest column in the table, and the one the removed columns mostly
        // paid for. The chip needs ~126; everything above that is for the
        // rejection reason on the sub-line under it, which truncated on every
        // row it appeared on while this was 126 -- and still did at 222.
        { text: "Stage", value: "stage", width: 308 },
        { text: "Clicked", value: "clicked", width: 76 },
        // A dd-mm-yyyy date is the widest fixed-width value in the table; at 88
        // it ran 4px past its cell on every single row.
        { text: "Applied", value: "applied_at", width: 96 },
        { text: "Updated", value: "last_modified", width: 88 },
        // Where this customer came from. The cell shows utm_source so the
        // column answers something on its own; the rest is behind the popup.
        { text: "UTM", value: "utm", width: 96 },
        // Copy UTM link. Narrow enough for one icon button; the sub-table
        // mirrors this colgroup, so its rows carry a matching empty cell.
        { text: "", value: "actions", width: 54, align: "end" },
      ],
      // Customers whose offer list is open. v-data-table owns this array and
      // fills it with the item OBJECTS themselves, not their ids -- it is
      // bound with `.sync`, so both ends write to it.
      expanded: [],
      filters: defaultFilters(),
      // Empty lists until the lookups land, so a slow fetch renders an empty
      // dropdown rather than breaking the panel.
      options: {
        cities: [], states: [], agents: [], agent_types: [],
        lead_partners: [], document_types: [], doc_statuses: [],
        perfios_statuses: [], availed_offer_counts: [],
        // The six attribution lists, from users/get_utm_dropdown/. Declared
        // here so the selects render empty rather than undefined on first paint.
        utm_sources: [], utm_mediums: [], utm_campaigns: [],
        utm_contents: [], utm_terms: [], utm_platforms: [],
      },
    };
  },
  computed: {
    /**
     * The filters to draw, in order, each marked for the visible row or the
     * panel. Anything locked or not currently applicable drops out first, so
     * the row closes up rather than leaving a hole.
     */
    filterLayout() {
      const shown = FILTER_ORDER.filter(
        (f) =>
          !this.filterLocked("offer-management", f.key) &&
          (!f.when || f.when(this))
      );
      // Locking the date picker frees its track too, so one more filter can
      // come up. Only ever true for a restricted user.
      const slots =
        PRIMARY_SLOTS +
        (this.filterLocked("offer-management", "date_range") ? 1 : 0);
      return shown.map((f, i) => ({ ...f, rest: i >= slots }));
    },
    /** What FilterBar needs to know to show its More/Less button. */
    restCount() {
      return this.filterLayout.filter((f) => f.rest).length;
    },
    /* Only the product tabs this user is allowed. Unlocked, this is every tab,
       so nothing changes for the admin or an unrestricted member. */
    visibleProductCounts() {
      return this.allowedSegments(
        "offer-management", "product_type", this.productCounts, "label");
    },
    /**
     * "All customers" plus the offer-stage buckets, in StatStrip's shape.
     *
     * The total is the SUM of the buckets, not this.totalCount: totalCount is
     * the current query's row count, so it would drop to the selected bucket's
     * figure the moment one was pressed. The counts endpoint deliberately
     * ignores `stage` (see getApplications), so summing it always gives the
     * unfiltered total while still moving with every other filter. The sum is
     * exact rather than close: annotate_offer_stage is a CASE with a default,
     * so every customer lands in exactly one bucket.
     *
     * `label` is what is read on screen, `filter` is the canonical value the
     * server matches on -- keeping them apart is what lets displayLabel rename
     * a stage without changing what gets sent. `null` is the no-stage that
     * shows everyone.
     */
    bucketItems() {
      const stages = (this.counts || []).filter((c) => c.label);
      /* Nothing counted yet -- in flight, or the last attempt failed: draw the
         tabs anyway with their values unknown, rather than no tabs at all --
         the same fallback User Management uses. While loading, StatStrip draws
         one skeleton per item, so this also gives the loading state its shape. The labels are the canonical pipeline order, so
         no bucket and no number is invented, and every cell still filters the
         table. The total goes unknown with them: it is the SUM of the buckets,
         and a sum of nothing is not 0 here, it is not known. */
      const all =
        !stages.length
          ? [
              {
                label: "All customers",
                value: UNKNOWN,
                isText: true,
                filter: null,
              },
              ...APPLICATION_STAGES.map((stage) => ({
                label: displayLabel(stage),
                value: UNKNOWN,
                isText: true,
                filter: stage,
              })),
            ]
          : [
              {
                label: "All customers",
                value: stages.reduce(
                  (sum, c) => sum + (Number(c.count) || 0),
                  0
                ),
                filter: null,
              },
              ...stages.map((c) => ({
                label: displayLabel(c.label),
                value: c.count,
                filter: c.label,
              })),
            ];
      /* Narrowed to the stage this user is allowed rather than hidden, the
         same as the product tabs: the count is theirs to see. "All customers"
         goes too -- with a stage pinned it would be a second way to ask for
         everything -- and dropping `filter` is what makes StatStrip
         read-only. */
      const pinned = this.lockedValues("offer-management", "stage");
      if (!pinned) return all;
      const kept = all.filter((item) => pinned.indexOf(item.filter) !== -1);
      return kept.length
        ? kept.map((item) => ({
            label: item.label,
            value: item.value,
            isText: item.isText,
          }))
        : all;
    },
    activeChips() {
      return activeFilters(this.filters);
    },
    /** "Both" can land half-done, which neither single slot can express. */
    docStatusItems() {
      const base = this.options.doc_statuses || [];
      return this.filters.document_type === "both"
        ? base.concat(["Partial Uploaded"])
        : base;
    },
    /** Perfios only means something once an application is moving. */
    showPerfios() {
      return (
        this.stage === APPLICATION_STAGE.UNDER_PROCESS ||
        this.stage === APPLICATION_STAGE.DISBURSED
      );
    },
    dateRange() {
      return store.dateRange;
    },
  },
  created() {
    /* An admin can pin this member to one product. The default above comes
       from the query string and falls back to Personal Loan, which would land
       a Business-Loan-only member on a tab with nothing in it. */
    const pinned = this.lockedValues("offer-management", "product_type");
    if (pinned && pinned.indexOf(this.product) === -1) {
      this.product = pinned[0];
    }
    const pinnedStage = this.lockedValues("offer-management", "stage");
    if (pinnedStage) this.stage = pinnedStage[0];
    this.loadOptions();
    this.load();
  },
  methods: {
    /** Imported helpers are not visible to templates; expose it explicitly. */
    displayLabel,
    /** Reset one filter (and anything that depended on it) from the chip's x. */
    clearFilter(key) {
      this.filters = clearOne(this.filters, key);
      this.reload();
    },
    // The dependent filters are cleared through the spec rather than by hand,
    // so a stale value can never keep filtering from a hidden control.
    onAgentTypeChange() {
      if (this.filters.is_assigned !== true) {
        this.filters = clearOne(this.filters, "agent");
      }
      this.reload();
    },
    onDocTypeChange() {
      this.filters = clearOne(this.filters, "document_request_status");
      this.reload();
    },
    /** Row -> UTM popup. `utm` is the group normalise.precheckRow builds. */
    ViewUtm(item) {
      this.$refs.UtmDialog.openDialog(item.utm || item);
    },
    /**
     * The option list backing one attribution filter.
     *
     * "utm_source" -> "utm_sources". The dropdown endpoint names its lists in
     * the plural and the filter spec names its keys in the singular; this is the
     * one place that fact is written down on this screen.
     */
    utmOptionKey(key) {
      return key + "s";
    },
    /**
     * The filter dropdowns, cached in the shared store so they are fetched
     * once per session rather than once per visit. A failure leaves them empty
     * -- never breaks the screen.
     *
     * Two sources, merged rather than assigned: the panel's own filter options,
     * and the six UTM lists from users/get_utm_dropdown/ -- the same endpoint
     * User Management, Notifications and the legacy screens already use, so the
     * values offered here are the values those screens offer. Assigning either
     * one wholesale would drop the other, and whichever landed second would win.
     */
    loadOptions() {
      const apply = (data) => {
        this.options = { ...this.options, ...data };
      };

      if (store.filterOptions) apply(store.filterOptions);
      else
        api
          .getFilterOptions()
          .then((data) => {
            store.setFilterOptions(data);
            apply(data);
          })
          .catch(() => {});

      if (store.utmOptions) apply(store.utmOptions);
      else
        api
          .getUtmOptions()
          .then((data) => {
            store.setUtmOptions(data);
            apply(data);
          })
          .catch(() => {});
    },
    /** The report is this list, with these filters -- load()'s params, no paging. */
    downloadReport() {
      return api.exportApplications({
        search_query: this.search,
        date_range: this.dateRange,
        product_type: this.product,
        stage: this.stage,
        ...toParams(this.filters),
      });
    },
    /**
     * Is this still the newest request? Two requests are now in flight per
     * load, and a slow earlier one must not overwrite a fast later one --
     * the same guard User Management uses.
     */
    current(token) {
      return token === this._requestToken;
    },
    load() {
      const token = (this._requestToken = (this._requestToken || 0) + 1);
      this.loading = true;
      this.countsLoading = true;
      this.error = null;
      const { rows, counts } = api.getApplications({
        page_number: this.page,
        page_length: this.pageLength,
        search_query: this.search,
        date_range: this.dateRange,
        product_type: this.product,
        stage: this.stage,
        // toParams() is the ONLY writer of filter params -- see filters.js
        // for why that matters.
        ...toParams(this.filters),
      });

      rows
        .then((data) => {
          if (!this.current(token)) return;
          this.items = data.results;
          // Collapsed by default. `expanded` is cleared rather than left
          // alone because it holds item OBJECT references: keeping the old
          // array would leave stale objects marked open over fresh data.
          this.expanded = [];
          this.totalPages = data.total_page_count;
          this.totalCount = data.total_count;
        })
        .catch((err) => {
          if (!this.current(token)) return;
          this.error = err;
          this.items = [];
        })
        .finally(() => {
          if (this.current(token)) this.loading = false;
        });

      /* A failure here leaves the buttons and the tab numbers showing what they
         showed before rather than blanking: the catch does not touch `counts`,
         which is the same keep-last-good the tab numbers already had.
         getApplications used to swallow the failure and resolve to an empty
         list, which arrived here as a success and emptied the strip. */
      counts
        .then((data) => {
          if (!this.current(token)) return;
          this.counts = data.counts || [];
          // Keep the last good tab numbers rather than blanking them.
          this.productCounts = data.product_counts || this.productCounts;
        })
        // Nothing to do: `counts` keeps its last good value, and with none the
        // strip is already drawn from the stage list with its values unknown.
        .catch(() => {})
        .finally(() => {
          if (this.current(token)) this.countsLoading = false;
        });
    },
    reload() {
      this.page = 1;
      this.load();
    },
    onPage(page) {
      this.page = page;
      this.load();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    selectProduct(product) {
      this.product = product;
      this.reload();
    },
    // StatStrip resolves the press itself and emits null to clear, so this
    // just stores what it was given -- toggling again here would undo it.
    selectStage(stage) {
      this.stage = stage;
      this.page = 1;
      this.load();
    },
    onDateRange(range) {
      store.setDateRange(range);
    },
    resetFilters() {
      // One assignment clears every filter in the spec, so a filter added
      // later cannot be forgotten here.
      this.filters = defaultFilters();
      this.stage = null;
      this.search = "";
      store.setDateRange(defaultDateRange());
      this.reload();
    },
    /**
     * The offer lines to draw for one customer -- their offers, or a single
     * stand-in when they have none.
     *
     * Without it the sub-table iterates an empty array and an offerless
     * customer opens onto a blank panel, which reads as a load that failed
     * rather than as a fact about that customer. EMPTY_OFFER carries a null
     * id, which is what the "No offer yet" line keys off.
     */
    offerLines(row) {
      const offers = this.bucketOffers(row);
      return offers.length ? offers : [EMPTY_OFFER];
    },
    /**
     * The offers on this row that actually have a link to copy.
     *
     * Built off offerLines() so the menu lists exactly what the sub-table shows
     * for the pressed bucket -- pressing Disbursed narrows both. EMPTY_OFFER
     * carries no utm_link, so the "No offer yet" placeholder filters itself out
     * and the button falls to its disabled state.
     */
    linkableOffers(row) {
      return this.offerLines(row).filter((o) => o && o.utm_link);
    },
    /**
     * Copy one offer's tracking link.
     *
     * Goes through the shared helper: navigator.clipboard only exists in a
     * SECURE context and this panel is served over plain http, so calling it
     * directly does nothing at all there.
     */
    async copyUtmLink(offer) {
      if (!offer || !offer.utm_link) {
        this.showSnakeBar("error", "UTM Link Not Found");
        return;
      }
      const ok = await copyToClipboard(offer.utm_link);
      this.showSnakeBar(
        ok ? "success" : "error",
        ok ? "UTM Link Copied Successfully" : "Failed to copy UTM Link"
      );
    },
    /**
     * The offers the pressed bucket is about.
     *
     * Under Process and Disbursed name a state an individual OFFER is in, so
     * the row narrows to the offers in it -- pressing Disbursed used to open a
     * customer onto every offer they hold, only one of which was disbursed.
     * The other buckets describe the customer rather than one offer, so they
     * keep the full list.
     */
    bucketOffers(row) {
      const all = row.offers || [];
      if (this.stage === APPLICATION_STAGE.DISBURSED) {
        return all.filter((o) => o.disbursed);
      }
      if (this.stage === APPLICATION_STAGE.UNDER_PROCESS) {
        // A sub-status is only ever written by ops working a real file, so its
        // presence is what "the lender has this" means -- the same test the
        // offers endpoint uses to decide an application is in flight.
        return all.filter((o) => o.sub_status);
      }
      return all;
    },
    /* v-data-table hands the slot its own expand helper; the row click just
       flips it. Guarded because the slot data is absent on a programmatic
       call. */
    toggleRow(item, slotData) {
      if (slotData) slotData.expand(!slotData.isExpanded);
    },
  },
};
</script>

<style scoped>
.kf-user {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 4px 0;
}
.kf-user__avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--kf-brand-soft);
  color: var(--kf-brand);
  font-size: var(--kf-fs-label);
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.kf-user__name {
  font-size: var(--kf-fs-hint);
  font-weight: 600;
  color: #000;
  white-space: nowrap;
}
/* ------------------------------------------------------ offer sub-rows ---
   A nested table whose columns mirror the parent's, so each offer value sits
   under the heading that names it. Lighter ground and a left rail mark it as
   belonging to the customer above. */
.kf-subtable {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  background: var(--kf-surface-2);
}
.kf-subrow > td {
  padding: 6px 16px 6px 0;
  font-size: var(--kf-fs-meta);
  color: #000;
  vertical-align: middle;
  border-bottom: 1px solid #e3ebf3;
}
.kf-subrow:last-child > td {
  border-bottom: 1px solid var(--kf-border);
}
.kf-subrow:hover > td {
  background: #e9f1f9;
}
/* Sits under the chevron column and draws the rail tying offers to the row. */
.kf-subrow__rail {
  padding: 0 !important;
  border-left: 3px solid #194e76;
}
.kf-offer {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.kf-offer__logo {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  flex-shrink: 0;
  object-fit: contain;
  background: #ffffff;
}
.kf-offer__logo--none {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #cfd8dc;
}
.kf-offer__title {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.kf-date {
  font-size: var(--kf-fs-meta);
  color: #000;
  white-space: nowrap;
}

.kf-user__sub {
  font-size: var(--kf-fs-label);
  color: #000;
  /* Clip rather than wrap, for the same reason as .kf-stack__sub: under fixed
     layout a wrapped sub-line doubles its row's height and the rows stop
     scanning as a column. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* ------------------------------------------------- two-line cells ---
   Every cell that carries two facts uses this pair rather than a class per
   column. Lifted verbatim from User Management, which packs nine facts into a
   table this width the same way. Colour stays #000 on the sub-line: the size
   drop is what separates the two, and grey at this size stops reading. */
.kf-stack {
  min-width: 0;
  padding: 2px 0;
}
.kf-stack--end {
  text-align: right;
}
.kf-stack__sub {
  font-size: var(--kf-fs-label);
  color: #000;
  /* Clip rather than wrap. A sub-line that wraps makes its row twice the
     height of the ones around it and the column stops scanning as a column.
     The full value stays on the title attribute. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* City and the rejection reason are free text and will not always fit their
   column. Same deal: clip, keep the row one height, full value on the title. */
.kf-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kf-salary {
  font-weight: 600;
  color: #000;
}
.kf-salary__unit {
  font-size: var(--kf-fs-label);
  color: #000;
}
.kf-tag {
  display: inline-block;
  padding: 0 6px;
  border-radius: 4px;
  font-weight: 600;
  font-size: var(--kf-fs-label);
}
/* The borrower applied for the same product again within the hour: a real
   second offer, counted everywhere, tagged so nobody calls them twice
   (leads.repeats on the server). */
.kf-tag--repeat {
  color: #1d4ed8;
  background: #dbeafe;
  border: 1px solid #93c5fd;
}
.kf-yes {
  color: #00897b;
  font-weight: 600;
  font-size: var(--kf-fs-meta);
  white-space: nowrap;
}
.kf-reason {
  font-size: var(--kf-fs-label);
  color: #000;
  display: inline-block;
  /* Derived from the column: 112px wide, 16px of cell padding each side, so
     the ellipsis has to appear at 80px. If the column width changes, this
     changes with it -- otherwise the text is clipped by the cell edge with no
     "...", and the :title tooltip becomes the only way to tell a cut-off
     reason from a complete one. */
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.kf-muted {
  color: #000;
}

/* The UTM cell is the click target for the details popup, so it reads as a
   link rather than as plain text sitting in a column. */
.kf-utm {
  color: #00695c;
  font-size: var(--kf-fs-meta);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.kf-utm:hover {
  text-decoration: underline;
}
</style>

<style>
/* Unscoped and scoped to this page's table only: the sub-rows are a separate
   table, and they can only line up with the parent's columns if both honour
   the declared widths. */
.kf-apps-table .kf-table.v-data-table > .v-data-table__wrapper > table {
  table-layout: fixed;
}
.kf-apps-table .kf-table__scroll {
  overflow-x: auto;
}
/* Matches the sum of the declared header widths. Below this the wrapper
   scrolls; above it the columns share the extra space. Keep the two numbers
   in step -- a min-width larger than the sum is what forces a scrollbar onto
   screens that could otherwise fit the table.
   1514 rather than the old 1102: the five customer columns do not fit inside
   1102, so this table now scrolls below roughly 1580px of viewport, the 54px
   Copy column added to it, and the 96px UTM column added the last of it.
   Hand-kept -- unlike User Management, which sums its own headers. The scrollbar belongs to
   .kf-table__scroll above, so it is the table that scrolls sideways and never
   the page. */
.kf-apps-table .kf-table.v-data-table > .v-data-table__wrapper > table {
  min-width: 1514px;
}

/* Same row-end action cluster the Leads table uses, so one icon button sits
   the same distance from the edge on both screens. */
.kf-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
}
.kf-menu {
  font-size: var(--kf-fs-hint);
}
.kf-menu-head {
  height: 28px;
  font-size: var(--kf-fs-meta);
  font-weight: 600;
}
</style>
